import React, { useState, useEffect } from 'react';
import { ArrowLeft, Wrench, Gear, Clock, Warning } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MaintenanceMode = ({ onBack }) => {
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("We're doing some quick maintenance. Please check back shortly.");
  const [whoBlocked, setWhoBlocked] = useState('customers'); // 'handlers', 'customers', 'both'
  const [durationPreset, setDurationPreset] = useState('30'); // '15', '30', '60', 'custom'
  const [customMinutes, setCustomMinutes] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch current maintenance settings from backend
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/maintenance`);
      const data = response.data || {};
      setEnabled(!!data.enabled);
      if (data.message) setMessage(data.message);
      if (data.who_blocked) setWhoBlocked(data.who_blocked);
      if (data.expires_at) setExpiresAt(data.expires_at);
      if (data.duration_minutes) {
        const mins = String(data.duration_minutes);
        if (['15', '30', '60'].includes(mins)) {
          setDurationPreset(mins);
        } else {
          setDurationPreset('custom');
          setCustomMinutes(mins);
        }
      }
    } catch (error) {
      console.error('Failed to load maintenance settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleTurnOn = async () => {
    setSaving(true);
    try {
      const mins = durationPreset === 'custom' ? parseInt(customMinutes, 10) || 30 : parseInt(durationPreset, 10) || 30;
      await axios.post(`${API}/settings/maintenance`, {
        enabled: true,
        message,
        who_blocked: whoBlocked,
        duration_minutes: mins
      });
      toast.success('Maintenance mode turned ON!');
      await fetchSettings();
    } catch (error) {
      console.error('Failed to turn ON maintenance mode:', error);
      toast.error(error.response?.data?.detail || 'Failed to enable maintenance mode');
    } finally {
      setSaving(false);
    }
  };

  const handleTurnOff = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/maintenance`, {
        enabled: false,
        message,
        who_blocked: whoBlocked,
        duration_minutes: 0
      });
      toast.success('Maintenance mode turned OFF!');
      await fetchSettings();
    } catch (error) {
      console.error('Failed to turn OFF maintenance mode:', error);
      toast.error('Failed to disable maintenance mode');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
      {/* Header Bar - Green matching screenshot */}
      <div className="bg-[#23d366] text-white px-4 py-3 shadow-md flex items-center gap-3">
        <button onClick={onBack} className="p-1 hover:bg-black/10 rounded-full transition-colors">
          <ArrowLeft size={22} weight="bold" />
        </button>
        <div>
          <h1 className="text-base font-bold leading-tight">Maintenance Mode</h1>
          <p className="text-[11px] text-white/90">Admin Control Center</p>
        </div>
      </div>

      {/* Main Container */}
      <div className="flex-1 p-4 md:p-8 max-w-xl w-full mx-auto space-y-4">
        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2" />
            <p className="text-xs font-semibold">Loading settings...</p>
          </div>
        ) : (
          <>
            {/* Status Info Box */}
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex items-start gap-3">
              <div className="p-2 bg-slate-100 rounded-lg text-slate-700 mt-0.5">
                <Wrench size={20} weight="bold" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-slate-800">Maintenance Mode</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Currently <span className={`font-bold ${enabled ? 'text-amber-600' : 'text-slate-600'}`}>{enabled ? 'ON' : 'OFF'}</span>. Admins are never blocked.
                  {enabled && expiresAt && (
                    <span className="block mt-1 text-[11px] text-amber-700 font-medium">
                      Auto-off scheduled: {new Date(expiresAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm space-y-4 text-slate-800">
              {/* Message Input */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Message shown to users
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none resize-none text-slate-800"
                  placeholder="We're doing some quick maintenance. Please check back shortly."
                />
              </div>

              {/* Who is blocked? */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Who is blocked?
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setWhoBlocked('handlers')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                      whoBlocked === 'handlers'
                        ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Handlers only
                  </button>
                  <button
                    type="button"
                    onClick={() => setWhoBlocked('customers')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                      whoBlocked === 'customers'
                        ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Customers only
                  </button>
                  <button
                    type="button"
                    onClick={() => setWhoBlocked('both')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                      whoBlocked === 'both'
                        ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Handlers + Customers
                  </button>
                </div>
              </div>

              {/* Duration (auto-off) */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Duration (auto-off)
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setDurationPreset('15')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                      durationPreset === '15'
                        ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    15 min
                  </button>
                  <button
                    type="button"
                    onClick={() => setDurationPreset('30')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                      durationPreset === '30'
                        ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    30 min
                  </button>
                  <button
                    type="button"
                    onClick={() => setDurationPreset('60')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                      durationPreset === '60'
                        ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    1 hour
                  </button>
                  <button
                    type="button"
                    onClick={() => setDurationPreset('custom')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                      durationPreset === 'custom'
                        ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Custom
                  </button>
                </div>

                {durationPreset === 'custom' && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      placeholder="Minutes (e.g. 45)"
                      value={customMinutes}
                      onChange={(e) => setCustomMinutes(e.target.value)}
                      className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs w-36 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                    <span className="text-xs text-slate-500">minutes</span>
                  </div>
                )}

                <p className="text-[11px] text-slate-400 italic mt-1.5">
                  Custom = enter minutes. It also turns off automatically when time is up.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleTurnOn}
                  className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-lg shadow transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  <Wrench size={16} weight="bold" />
                  Turn ON
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleTurnOff}
                  className="flex-1 py-2.5 bg-slate-400 hover:bg-slate-500 text-white font-bold text-xs rounded-lg shadow transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  <Gear size={16} weight="bold" />
                  Turn OFF
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MaintenanceMode;
